/**
 * Late timesheets — decision 7 (Wimpie, 2026-10-25): a week is due on the day
 * and time a manager sets (default Monday 10:00 for the week before); late
 * weeks get a reminder, nothing is blocked, and managers see this log
 * (T&B-1 PR 4).
 *
 * Derived, never typed in (spec, "Controls"): the server takes each week's due
 * time from the setting in force when the week ended, and judges the week as
 * it stood at that moment, rebuilt from the database's own event rows. So a
 * week submitted empty and filled in after the deadline is late, while a
 * manager's return, a correction or a deletion afterwards never makes an
 * on-time week late (server/src/time-reports.ts says exactly how). Managers
 * only.
 */

import { useCallback, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { api, type AuthState, type LateReport, type ObligationTypeInUse } from '../api'
import { AppShell } from '../components/AppShell'
import { ErrorState, LoadingState } from '../components/States'
import { inputCls, labelCls, todaySast } from '../components/TimeCapture'
import { downloadCsv } from '../csv'
import { sectionCls, tdCls, thCls, Toolbar, useReport } from './ReportsPage'

const when = (iso: string) =>
  new Date(iso).toLocaleString('en-ZA', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
const dayHead = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })

export function lateWords(minutes: number): string {
  if (minutes < 60) return `${minutes} min`
  if (minutes < 60 * 48) return `${Math.floor(minutes / 60)} h ${minutes % 60} min`
  return `${Math.floor(minutes / 1440)} days`
}

const STATUS: Record<'on_time' | 'late' | 'outstanding', [string, string]> = {
  on_time: ['On time', 'bg-eq-success-bg text-eq-success'],
  late: ['Late', 'bg-eq-warning-bg text-eq-warning'],
  outstanding: ['Not submitted', 'bg-eq-danger-bg text-eq-danger'],
}

export function LateTimesheetsPage({ obligationTypes, auth }: { obligationTypes: ObligationTypeInUse[]; auth: AuthState }) {
  const [params, setParams] = useSearchParams()
  const month = params.get('month') ?? todaySast().slice(0, 7)
  const [showAll, setShowAll] = useState(false)
  const signedInManager = auth.mode === 'cognito' && auth.user?.role === 'manager'
  const load = useCallback(() => (signedInManager ? api.lateTimesheets(month) : Promise.reject(new Error('Only a manager may see late timesheets.'))), [month, signedInManager])
  const { data, error } = useReport<LateReport>(load)
  const shown = data?.weeks.filter((w) => showAll || w.status !== 'on_time') ?? []

  const csv = () => {
    if (data === null) return
    downloadCsv(
      `late-timesheets-${month}.csv`,
      ['Person', 'Week of', 'Due', 'Submitted', 'Status', 'Late by (minutes)', 'Hours logged', 'Items'],
      shown.map((w) => [w.person.name, w.weekStart, w.dueAt, w.submittedAt ?? '', STATUS[w.status][0], String(w.lateMinutes), w.hours, String(w.items)])
    )
  }

  return (
    <AppShell
      obligationTypes={obligationTypes}
      auth={auth}
      eyebrow="Time"
      title="Late timesheets"
      subtitle="Each person-week submitted after its due time, or not submitted at all. Derived from submission times and the due time then in force — never typed in."
    >
      {auth.mode === 'anonymous' ? (
        <ErrorState message="The log shows everyone's weeks, so it needs sign-in. This server is running without Cognito (local development)." />
      ) : !signedInManager ? (
        <ErrorState message="Only a manager may see late timesheets." />
      ) : (
        <>
          <Toolbar onCsv={csv}>
            <label className={labelCls}>
              Weeks starting in
              <input type="month" className={inputCls} value={month} onChange={(e) => setParams(e.target.value ? { month: e.target.value } : {}, { replace: true })} />
            </label>
            <label className="flex items-center gap-1.5 pb-1 text-[13px]">
              <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} />
              Show weeks on time too
            </label>
          </Toolbar>
          {error ? (
            <ErrorState message={error} />
          ) : data === null ? (
            <LoadingState what="the log" />
          ) : (
            <section className={sectionCls}>
              {data.startsFrom === null ? (
                <p className="m-0 text-[14px] text-eq-neutral">Nobody has submitted a timesheet yet, so there is nothing to assess.</p>
              ) : (
                <>
                  <p className="mb-3 mt-0 text-[12.5px] text-eq-helper">
                    Weeks due since {dayHead(data.startsFrom)}, the practice&rsquo;s first timesheet submission. A week is expected of anyone
                    whose access and working week cover a working day in it, including people whose access has since ended, or who logged
                    something in it.
                  </p>
                  {shown.length === 0 ? (
                    <p className="m-0 text-[14px] text-eq-neutral">
                      {data.weeks.length === 0 && data.startsFrom.slice(0, 7) > month
                        ? 'Every week starting in this month was due before the log began.'
                        : showAll
                          ? 'No weeks due in this month.'
                          : 'Nothing late or outstanding for weeks starting in this month.'}
                    </p>
                  ) : (
                    <table className="w-full border-collapse text-[13.5px]">
                      <thead className="text-[12px] text-eq-neutral">
                        <tr>
                          <th className={thCls}>Person</th>
                          <th className={thCls}>Week of</th>
                          <th className={thCls}>Due</th>
                          <th className={thCls}>Submitted</th>
                          <th className={thCls}>Status</th>
                          <th className={`${thCls} text-right`}>Late by</th>
                          <th className={`${thCls} text-right`}>Hours</th>
                        </tr>
                      </thead>
                      <tbody>
                        {shown.map((w) => (
                          <tr key={`${w.person.id}|${w.weekStart}`} className="border-t border-eq-border">
                            <td className={tdCls}>{w.person.name}</td>
                            <td className={`${tdCls} whitespace-nowrap`}>{dayHead(w.weekStart)}</td>
                            <td className={`${tdCls} whitespace-nowrap`}>{when(w.dueAt)}</td>
                            <td className={`${tdCls} whitespace-nowrap`}>{w.submittedAt === null ? '—' : when(w.submittedAt)}</td>
                            <td className={tdCls}>
                              <span className={`rounded-eq-pill px-2 py-0.5 text-[11px] font-semibold ${STATUS[w.status][1]}`}>{STATUS[w.status][0]}</span>
                            </td>
                            <td className={`${tdCls} text-right tabular-nums`}>{w.status === 'on_time' ? '' : lateWords(w.lateMinutes)}</td>
                            <td className={`${tdCls} text-right tabular-nums`}>{w.hours}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  {data.notAssessed.length > 0 && (
                    <p className="mb-0 mt-3 text-[12.5px] text-eq-helper">
                      No working week is set for {data.notAssessed.map((p) => p.name).join(', ')}, so their weeks with nothing logged cannot be
                      judged and are left out. Set it under Jobs and prices → Rates and hours.
                    </p>
                  )}
                </>
              )}
            </section>
          )}
        </>
      )}
    </AppShell>
  )
}
